import toRadian from 'helpers/toRadian'
import rotateIconPath from 'images/rotateIcon'

export default class extends google.maps.OverlayView {
  constructor({ map, id, imageSize, imageSrc, location, floorPlanLoadedAction, opacity }) {
    super()
    this.map = map
    this.id = id
    this.imageSize = imageSize
    this.imageSrc = imageSrc
    this.nw = location.nw
    this.se = location.se
    this.containerElementDisplay = 'block'
    this.xform = this.createImageOverlayTransform({ latLng: this.nw, imgPt: this.getImgCornerPt('nw') }, { latLng: this.se, imgPt: this.getImgCornerPt('se') })
    this.ne = this.xform.transform(this.imageSize.width, 0)
    this.sw = this.xform.transform(0, this.imageSize.height)
    this.containerElement = null
    this.floorPlanLoadedAction = floorPlanLoadedAction
    this.opacity = opacity || 1
    this.setMap(this.map)
    this.initCornerMarkers()
    this.initRectangle()
    this.initRotateControl()
  }

  buildDragPath(position, heading) {
    const pathPts = []
    const numberOfPoints = 1000

    let index = numberOfPoints + 1
    while ((index -= 1) > 0) {
      pathPts[index - 1] = google.maps.geometry.spherical.computeOffset(position, index / 10, heading)
    }

    index = numberOfPoints + 1
    while ((index -= 1) > 0) {
      pathPts[numberOfPoints + index - 1] = google.maps.geometry.spherical.computeOffset(position, index / 10, 0 - (180 - heading))
    }

    return pathPts
  }

  findClosestPoint(position, pathPts) {
    const distances = []
    const distanceKeys = []

    pathPts.forEach((pathPt, index) => {
      const d = google.maps.geometry.spherical.computeDistanceBetween(position, pathPt)
      distances[index] = d
      distanceKeys[d] = index
    })

    return typeof pathPts[distanceKeys[Math.min.apply(Math, distances)] + 1] === 'undefined' ? pathPts[distanceKeys[Math.min.apply(Math, distances)]] : pathPts[distanceKeys[Math.min.apply(Math, distances)] + 1]
  }

  initCornerMarkers() {
    ['nw', 'se', 'ne', 'sw'].forEach(corner => {
      let marker = this[`${corner}Marker`] = new google.maps.Marker({
        position: this[corner],
        icon: {
          url: 'https://maps.google.com/mapfiles/kml/pal4/icon57.png',
          origin: new google.maps.Point(0, 0),
          anchor: new google.maps.Point(15, 15),
          scaledSize: new google.maps.Size(30, 30)
        },
        zIndex: 101,
        map: this.map,
        draggable: true,
        raiseOnDrag: false
      })

      // Set some additional attributes
      marker.ptId = corner
      marker.imgPt = this.getImgCornerPt(corner)

      // Bind the drag event
      google.maps.event.addListener(marker, 'drag', e => this.cornerMarkerDragEventHandler(marker))
      google.maps.event.addListener(marker, 'dragend', e => {
        marker.setPosition(this.findClosestPoint(marker.getPosition(), this.resizePath))
        google.maps.event.trigger(this, 'location_changed', this.getPosition())
      })

      google.maps.event.addListener(marker, 'mousedown', e => {
        this.setMarkerVisibleSoft(false)
        google.maps.event.trigger(this, 'mousedown', e)
      })
      return google.maps.event.addListener(marker, 'mouseup', e => {
        this.setMarkerVisibleSoft(true)
        google.maps.event.trigger(this, 'mouseup', e)
      })
    })

    return [this.nwMarker, this.seMarker, this.neMarker, this.swMarker].forEach(marker => {
      let diagonalMarkers = this.getDiagonalMarkers(marker)
      google.maps.event.addDomListener(marker, 'mousedown', e => {
        let heading = google.maps.geometry.spherical.computeHeading(diagonalMarkers[0].getPosition(), diagonalMarkers[1].getPosition())
        this.resizePath = this.buildDragPath(marker.getPosition(), heading)
      })
    })
  }

  initRectangle() {
    this.rectangle = new (google.maps.Polygon)({
      path: this.buildPathForRectangle(),
      strokeColor: '#4CE91C',
      draggable: true,
      strokeOpacity: 0,
      strokeWeight: 2,
      fillOpacity: 0,
      zIndex: 101,
      map: this.map
    })
    google.maps.event.addListener(this.rectangle, 'drag', this.rectangleDragEventHandler.bind(this))
    google.maps.event.addListener(this.rectangle, 'mousedown', e => google.maps.event.trigger(this, 'mousedown', e))
    google.maps.event.addListener(this.rectangle, 'mouseup', e => google.maps.event.trigger(this, 'mouseup', e))
    google.maps.event.addListener(this.rectangle, 'contextmenu', e => google.maps.event.trigger(this, 'contextmenu', e))

    google.maps.event.addListener(this.rectangle, 'dragstart', e => {
      this.setTransparent(true)
      this.setMarkerVisible(false)
    })
    google.maps.event.addListener(this.rectangle, 'dragend', e => {
      this.setRotateControlPosition()
      this.setMarkerVisible(true)
      google.maps.event.trigger(this, 'location_changed', this.getPosition())
      this.setTransparent(false)
    })

    // pass click event down to the map
    // (allows placing of labels, https://github.com/ipvideomarketinfo/calculator-v3/issues/298)
    google.maps.event.addListener(this.rectangle, 'click', e => {
      if (this.map.draggableCursor && this.map.draggableCursor === 'crosshair') {
        google.maps.event.trigger(this.getMap(), 'click', e)
      }
    })
  }

  calculateRotateControlPosition() {
    const nwMarkerPosition = this.nwMarker.getPosition()
    const swMarkerPosition = this.swMarker.getPosition()
    const projection = this.getProjection()
    const rotateControlDistance = this.map.getZoom() < 17 ? 30 : 50

    const distanceToRotateControl = google.maps.geometry.spherical.computeDistanceBetween(nwMarkerPosition, swMarkerPosition) / 2
    const heading = google.maps.geometry.spherical.computeHeading(nwMarkerPosition, swMarkerPosition)
    const positionBetweenMarkers = google.maps.geometry.spherical.computeOffset(this.nwMarker.getPosition(), distanceToRotateControl, heading)
    let { x, y } = projection.fromLatLngToDivPixel(positionBetweenMarkers)

    return {
      positionBetweenMarkers,
      rotateControlPosition: projection.fromDivPixelToLatLng(
        new google.maps.Point(
          x += rotateControlDistance * Math.cos(toRadian(heading)),
          y += rotateControlDistance * Math.sin(toRadian(heading))
        )
      )
    }
  }

  setRotateControlPosition() {
    if (EXPORT) return
    const { positionBetweenMarkers, rotateControlPosition } = this.calculateRotateControlPosition()
    this.rotateControl.setPosition(rotateControlPosition)
    this.dashedLine.setPath([positionBetweenMarkers, rotateControlPosition])
  }

  initRotateControl() {
    this.rotateControl = new google.maps.Marker({
      // don't care about position on initialize,
      // set it in onAdd
      position: { lat: 1, lng: 1 },
      icon: {
        url: rotateIconPath,
        origin: new google.maps.Point(0, 0),
        anchor: new google.maps.Point(10, 10),
        scaledSize: new google.maps.Size(20, 20)
      },
      zIndex: 101,
      map: this.map,
      draggable: true,
      raiseOnDrag: false
    })

    this.dashedLine = new google.maps.Polyline({
      // don't care about position on initialize,
      // set it in onAdd
      path: [{ lat: 1, lng: 1 }, { lat: 1, lng: 1 }],
      strokeOpacity: 0,
      icons: [{
        icon: {
          path: 'M 0,.8 0,-.8',
          strokeOpacity: 1,
          scale: 4,
          anchor: new google.maps.Point(0, 2)
        },
        offset: '0',
        repeat: '20px'
      }],
      map: this.map
    })

    google.maps.event.addListener(this.rotateControl, 'dragstart', e => {
      this.rectangle.set('clickable', false)
      this.setMarkerVisibleSoft(false)
      this.originalAngle = google.maps.geometry.spherical.computeHeading(this.getBounds().getCenter(), e.latLng)
      this.origin = google.maps.geometry.spherical.computeHeading(this.neMarker.getPosition(), this.swMarker.getPosition())
      this.setTransparent(true)
    })

    google.maps.event.addListener(this.rotateControl, 'drag', e => {
      const rawRotationAngle = google.maps.geometry.spherical.computeHeading(this.getBounds().getCenter(), e.latLng)
      let a = rawRotationAngle - this.originalAngle
      a += (a > 180) ? -360 : (a < -180) ? 360 : 0
      this.rotateByDeg(a, this.origin)
    })

    google.maps.event.addListener(this.rotateControl, 'dragend', e => {
      this.setRotateControlPosition()
      this.setMarkerVisibleSoft(true)
      this.map.setOptions({ draggable: true })
      this.rectangle.set('clickable', true)
      google.maps.event.trigger(this, 'location_changed', this.getPosition())
      this.setTransparent(false)
    })
  }

  rotateByDeg(deg, originalAngle = google.maps.geometry.spherical.computeHeading(this.neMarker.getPosition(), this.swMarker.getPosition())) {
    let originalDistance = google.maps.geometry.spherical.computeDistanceBetween(this.swMarker.getPosition(), this.neMarker.getPosition())

    let swPosition = google.maps.geometry.spherical.computeOffset(this.getBounds().getCenter(), originalDistance / 2, originalAngle + deg)
    let nePosition = google.maps.geometry.spherical.computeOffset(this.getBounds().getCenter(), originalDistance / 2, 0 - (180 - (originalAngle + deg)))

    this.swMarker.setPosition(swPosition)
    this.neMarker.setPosition(nePosition)

    this.xform = this.createImageOverlayTransform(
      { latLng: swPosition, imgPt: this.swMarker.imgPt },
      { latLng: nePosition, imgPt: this.neMarker.imgPt }
    )

    this.seMarker.setPosition(this.xform.transform(this.seMarker.imgPt.x, this.seMarker.imgPt.y))
    this.nwMarker.setPosition(this.xform.transform(this.nwMarker.imgPt.x, this.nwMarker.imgPt.y))

    this.rectangle.setPath(this.buildPathForRectangle())
    this.draw()
  }

  resizeByScale(scale) {
    let originalAngle
    if (!originalAngle) { originalAngle = google.maps.geometry.spherical.computeHeading(this.neMarker.getPosition(), this.swMarker.getPosition()) }
    let originalDistance = google.maps.geometry.spherical.computeDistanceBetween(this.swMarker.getPosition(), this.neMarker.getPosition())

    let newDistance = originalDistance * scale

    let swPosition = google.maps.geometry.spherical.computeOffset(this.getBounds().getCenter(), newDistance / 2, originalAngle)
    let nePosition = google.maps.geometry.spherical.computeOffset(this.getBounds().getCenter(), newDistance / 2, 0 - (180 - originalAngle))

    this.swMarker.setPosition(swPosition)
    this.neMarker.setPosition(nePosition)

    this.xform = this.createImageOverlayTransform(
      { latLng: swPosition, imgPt: this.swMarker.imgPt },
      { latLng: nePosition, imgPt: this.neMarker.imgPt }
    )

    this.seMarker.setPosition(this.xform.transform(this.seMarker.imgPt.x, this.seMarker.imgPt.y))
    this.nwMarker.setPosition(this.xform.transform(this.nwMarker.imgPt.x, this.nwMarker.imgPt.y))

    this.rectangle.setPath(this.buildPathForRectangle())
    this.map.fitBounds(this.getBounds())
    this.draw()
    return google.maps.event.trigger(this, 'location_changed', this.getPosition())
  }

  getBounds() {
    let bounds = new google.maps.LatLngBounds()
    this.buildPathForRectangle().forEach(location => bounds.extend(location))
    return bounds
  }

  getAdjacentDistance() {
    return google.maps.geometry.spherical.computeDistanceBetween(this.swMarker.getPosition(), this.neMarker.getPosition())
  }

  setMarkerVisible(value) {
    [this.nwMarker, this.neMarker, this.seMarker, this.swMarker, this.rotateControl].forEach(el => {
      if (el) el.setVisible(value)
    })
    if (this.dashedLine) value ? this.dashedLine.setMap(this.map) : this.dashedLine.setMap(null)
  }

  setDraggable(value) {
    this.rectangle.setOptions({ draggable: value })
  }

  setMarkerVisibleSoft(value) {
    [this.nwMarker, this.neMarker, this.seMarker, this.swMarker].forEach(el => {
      el.setOpacity(value ? 1 : 0)
    })
    this.rotateControl.set('icon', {...this.rotateControl.getIcon(), url: value ? rotateIconPath : null })
    value ? this.dashedLine.setMap(this.map) : this.dashedLine.setMap(null)
  }

  /**
   * Handle the corner marker drag event.
   * @param marker: The marker which was moved
   */
  cornerMarkerDragEventHandler(marker) {
    let diagonalMarkers = this.getDiagonalMarkers(marker)
    let adjacentMarkers = this.getAdjacentMarkers(marker)

    marker.setPosition(this.findClosestPoint(marker.getPosition(), this.resizePath))
    let heading = google.maps.geometry.spherical.computeHeading(diagonalMarkers[0].getPosition(), diagonalMarkers[1].getPosition())
    this.resizePath = this.buildDragPath(marker.getPosition(), heading)

    this.xform = this.createImageOverlayTransform(
      { latLng: diagonalMarkers[0].getPosition(), imgPt: diagonalMarkers[0].imgPt },
      { latLng: diagonalMarkers[1].getPosition(), imgPt: diagonalMarkers[1].imgPt }
    )

    adjacentMarkers[0].setPosition(this.xform.transform(adjacentMarkers[0].imgPt.x, adjacentMarkers[0].imgPt.y))
    adjacentMarkers[1].setPosition(this.xform.transform(adjacentMarkers[1].imgPt.x, adjacentMarkers[1].imgPt.y))

    this.rectangle.setPath(this.buildPathForRectangle())
    this.draw()
    this.setRotateControlPosition()
  }

  getDiagonalMarkers(marker) {
    switch (marker.ptId) {
      case 'nw': case 'se':
        return [this.nwMarker, this.seMarker]
      case 'ne': case 'sw':
        return [this.neMarker, this.swMarker]
    }
  }

  getDiagonalMarker(marker) {
    switch (marker.ptId) {
      case 'nw': return this.seMarker
      case 'se': return this.nwMarker
      case 'ne': return this.swMarker
      case 'sw': return this.neMarker
    }
  }

  getAdjacentMarkers(marker) {
    switch (marker.ptId) {
      case 'nw': case 'se':
        return [this.neMarker, this.swMarker]
      case 'ne': case 'sw':
        return [this.nwMarker, this.seMarker]
    }
  }

  /**
   * Handle the rectangle drag event.
   */
  rectangleDragEventHandler() {
    let points = this.rectangle.getPath().getArray()
    this.nwMarker.setPosition(points[0])
    this.neMarker.setPosition(points[1])
    this.seMarker.setPosition(points[2])
    this.swMarker.setPosition(points[3])
    this.draw()
  }

  /**
   * Build an array of latLng points for use when drawing the rectangle
   */
  buildPathForRectangle() {
    return [
      this.nwMarker.getPosition(),
      this.neMarker.getPosition(),
      this.seMarker.getPosition(),
      this.swMarker.getPosition(),
      this.nwMarker.getPosition()
    ]
  }

  /**
   * Determine the x/y pixle position for a corner of the image
   * @param corner: The ID of the desired corner position
   */
  getImgCornerPt(corner) {
    switch (corner) {
      case 'nw': return { x: 0, y: 0 }
      case 'se': return { x: this.imageSize.width, y: this.imageSize.height }
      case 'ne': return { x: this.imageSize.width, y: 0 }
      case 'sw': return { x: 0, y: this.imageSize.height }
    }
  }

  getPosition() {
    return { nw: this.nwMarker.getPosition(), se: this.seMarker.getPosition() }
  }

  /**
   * Called whn the overlay can attach itself to the map.
   * An overlay's receipt of onAdd() indicates that
   * the map's panes are available for attaching
   * the overlay to the map via the DOM.
   */

  onAdd() {
    let div = document.createElement('div')
    div.style.border = 'none'
    div.style.borderWidth = '0px'
    div.style.position = 'absolute'
    div.style.cursor = 'move'
    this.addImage(div)
    this.containerElement = div
    this.setTransparent(false)
    let panes = this.getPanes()
    this.setRotateControlPosition()
    return panes.overlayLayer.appendChild(div)
  }

  addImage(div) {
    let img = document.createElement('img')
    img.style.width = '100%'
    img.style.height = '100%'
    this.imgElement = img
    if (EXPORT) img.onload = this.floorPlanLoadedAction
    img.src = this.imageSrc
    div.appendChild(img)
  }

  /**
   * Sets transformaion parameters for the overlay. If the transformation causes the image
   * to be larger than 10,000 pixels along an axis, the image is hidden.
   */

  draw() {
    let div = this.containerElement
    let overlayProjection = this.getProjection()
    let sw = overlayProjection.fromLatLngToDivPixel(this.swMarker.getPosition())
    let ne = overlayProjection.fromLatLngToDivPixel(this.neMarker.getPosition())
    let dxO = ne.x - (sw.x)
    let dyO = sw.y - (ne.y)
    div.style.display = 'block'
    let dxI = this.imageSize.width
    let dyI = this.imageSize.height
    let r = Math.atan2(dyO, dxO) - Math.atan2(dyI, dxI)
    let scale = Math.sqrt((dxO * dxO) + (dyO * dyO)) / Math.sqrt((dxI * dxI) + (dyI * dyI))

    // Resize the images DIV to fit the indicated dimensions.
    div.style.display = this.containerElementDisplay
    div.style.left = sw.x + 'px'
    div.style.top = (sw.y - (scale * this.imageSize.height)) + 'px'
    div.style.width = (scale * this.imageSize.width) + 'px'
    div.style.height = (scale * this.imageSize.height) + 'px'
    let transform = `rotate(${-r}rad)`
    div.style.WebkitTransform = transform
    div.style.MozTransform = transform
    div.style.transform = transform
    let tOrigin = '0% 100%'
    div.style.transformOrigin = tOrigin
    div.style.WebkitTransformOrigin = tOrigin
    div.style.MozTransformOrigin = tOrigin

    if (this.rotateControl.getVisible() && this.rotateControl.getIcon().url) {
      this.setRotateControlPosition()
    }

    // Todo: decide what data should be passed to the event handler
    return google.maps.event.trigger(this, 'draw', null)
  }

  setTransparent(bool) {
    this.containerElement.style.opacity = ((bool ? 0.6 : 1) * this.opacity).toString()
  }

  updateOpacity(value) {
    this.opacity = value
    this.setTransparent(false)
  }

  /**
   * Removes this overlay from the map.
   */

  onRemove() {
    this.containerElement.parentNode.removeChild(this.containerElement)
    this.containerElement = null
    google.maps.event.removeListener(this.zoomListener)
    const markers = [this.nwMarker, this.neMarker, this.seMarker, this.swMarker, this.nwMarker,
      this.rectangle, this.rotateControl, this.dashedLine]
    markers.forEach(function(el) {
      el.setMap(null)
      el = null
    })
  }

  /**
   * Creates an image overlay transform from two point pairs and an image.
   *
   * @param p0: The first reference point
   * @param p1: The second reference point
   */

  createImageOverlayTransform(p0, p1) {
    let lat0 = p0.latLng.lat()
    let lon0 = p0.latLng.lng()
    let lat1 = p1.latLng.lat()
    let lon1 = p1.latLng.lng()
    let dlat = lat1 - lat0
    let dlon = lon1 - lon0
    let dx = p1.imgPt.x - (p0.imgPt.x)
    let dy = p1.imgPt.y - (p0.imgPt.y)
    if ((Math.abs(dlat) < 0.0000001) && (Math.abs(dlon) < 0.0000001)) {
      this.containerElementDisplay = 'none'
      // throw new Error('Reference pairs may not coincide. At least one of the lat/lon deltas must be greater than 0.0000001.')
    } else {
      this.containerElementDisplay = 'block'
    }
    if ((Math.abs(dx) < 1) && (Math.abs(dy) < 1)) {
      throw new Error('Reference points may not coincide. At least one of the x/y deltas must be greater than 0.')
    }
    let avgLat = lat0 + (dlat / 2)
    let r = -(Math.atan2(-dy, dx) - Math.atan2(dlat, dlon * Math.cos((avgLat * Math.PI) / 180)))
    let lonpp
    if (Math.abs(dlon) > 0.0000001) {
      lonpp = dlon / ((Math.cos(r) * dx) + (Math.cos(r + (Math.PI / 2)) * -dy))
    }
    let latpp
    if (Math.abs(dlat) > 0.0000001) {
      latpp = dlat / ((Math.sin(r) * dx) + (Math.sin(r + (Math.PI / 2)) * -dy))
    }
    if (latpp === undefined) {
      latpp = lonpp * Math.cos((avgLat * Math.PI) / 180)
    }
    if (lonpp === undefined) {
      lonpp = latpp / Math.cos((avgLat * Math.PI) / 180)
    }
    let xform = {
      r,
      latpp,
      lonpp,
      transform(x, y) {
        let dxi = x - (p0.imgPt.x)
        let dyi = y - (p0.imgPt.y)
        return new (google.maps.LatLng)(lat0 + (Math.sin(r) * dxi * latpp) + (Math.sin(r + (Math.PI / 2)) * -dyi * latpp), lon0 + (Math.cos(r) * dxi * lonpp) + (Math.cos(r + (Math.PI / 2)) * -dyi * lonpp))
      }
    }
    return xform
  }
}



// WEBPACK FOOTER //
// ./src/components/FloorPlan/FloorPlanOverlay/index.js